/**
 * Consumable + currency icon family — World Art Pack 2, Task 6 (final lane).
 *
 * One procedural generator per consumable `IconKind` (8 kinds, covering every
 * `type === 'consumable'` item in ITEM_DATABASE) PLUS the `coin` kind for
 * `type === 'currency'`. Coin lives HERE rather than in its own file — a
 * single-kind family is too small to justify a fourth generator module, and
 * consumables are its closest stackable-item siblings (decision per the
 * Task-6 brief's "your call, document it"). Its palette deliberately matches
 * the bespoke `CurrencyIcons.ts` gold (`#FFD700`/`#DAA520`/`#B8860B`).
 *
 * Each painter draws flat, silhouette-first inner art in the central 24x24
 * space per `docs/design/coil-art/ICON_DESIGN_SHEETS.md` (2-3 value steps,
 * top-left light). Front-facing objects — no hero diagonal (food's drumstick
 * leans for energy, but that's composition, not the weapon-diag convention).
 * `framedIcon` owns ALL framing and returns the base64 data URL the registry
 * ladder expects.
 *
 * Kind anchors (per the Task-6 brief): potion = round-bottom flask (liquid
 * takes `look.tint`, DEFAULT RED) · elixir = tall slim vial · flask = wide
 * flask w/ stopper · scroll = rolled parchment w/ ribbon · food = drumstick ·
 * bandage = wrapped roll · candy = wrapped sweet · stone_consumable = runed
 * stone · coin = CURRENCY_ICONS-style gold coin.
 *
 * Squint-trap separation (potion vs elixir vs flask): three DIFFERENT
 * silhouettes (round bulb / tall slim column / wide low triangle) AND three
 * different default liquid hues (red / gold / teal). Only the potion takes
 * `look.tint` — the fixed elixir/gold + flask/teal hues are what keep the
 * glassware trio apart at 20px, so they deliberately ignore tint.
 *
 * Registration happens in ItemIconRegistry.ts (the single composition seam);
 * this file only imports a TYPE from the registry, so there is no runtime
 * import cycle.
 */
import { framedIcon } from './rarityFrame';
import { inferIconLook, type IconKind, type IconLook } from './iconLook';
import type { ProceduralIconGenerator } from '../ItemIconRegistry';
import type { ItemDefinition } from '@/shared/content/generated/items.gen';

// --- shared palette (2-3 value steps, top-left light) ------------------------
const GLASS = '#dfe7f2';
const GLASS_EDGE = '#aebbd0';
const CORK = '#8b7355';
const CORK_DARK = '#6e5a42';
/** Default potion liquid red when the item carries no elemental tint. */
export const POTION_LIQUID_DEFAULT = '#d43a3a';
const ELIXIR_LIQUID = '#ffd76b';
const FLASK_LIQUID = '#4ecdc4';
const PARCHMENT = '#e8d8a0';
const PARCHMENT_LIGHT = '#f4ead0';
const PARCHMENT_DARK = '#c8b880';
const RIBBON = '#c0392b';
const MEAT = '#a9663a';
const MEAT_LIGHT = '#c98a55';
const BONE = '#f2ead8';
const LINEN = '#e9e2d2';
const LINEN_DARK = '#cbc2ab';
const CANDY = '#ff8fb3';
const CANDY_LIGHT = '#ffb7cd';
const CANDY_DARK = '#d96a92';
const RUNESTONE = '#6b7280';
const RUNESTONE_LIGHT = '#8b93a2';
const RUNESTONE_DARK = '#4d5460';
const RUNE_GLOW = '#5ff2b0';
// CurrencyIcons.ts gold family — keep coin visually consistent with bespoke `gold`.
const COIN_FACE = '#FFD700';
const COIN_EDGE = '#DAA520';
const COIN_DEEP = '#B8860B';

type KindPainter = (look: IconLook, size: number) => string;

// --- potion — round-bottom flask, liquid takes look.tint (default red) -------
const potion: KindPainter = (look, size) => {
  const liquid = look.tint ?? POTION_LIQUID_DEFAULT;
  return framedIcon(`
    <circle cx="12" cy="14" r="6.3" fill="${GLASS}" opacity="0.55"/>
    <path d="M5.9 13.4 A6.15 6.15 0 1 0 18.1 13.4 Q12 15.2 5.9 13.4 Z" fill="${liquid}"/>
    <circle cx="12" cy="14" r="6.3" fill="none" stroke="${GLASS_EDGE}" stroke-width="1"/>
    <rect x="10.5" y="4.6" width="3" height="4.2" fill="${GLASS}" opacity="0.85"/>
    <rect x="9.9" y="4.2" width="4.2" height="1.3" rx="0.65" fill="${GLASS_EDGE}"/>
    <rect x="10.3" y="2" width="3.4" height="2.4" rx="0.9" fill="${CORK}"/>
    <circle cx="9.4" cy="16.4" r="0.8" fill="#ffffff" opacity="0.55"/>
    <circle cx="11.2" cy="18" r="0.55" fill="#ffffff" opacity="0.45"/>
    <path d="M7.4 11.2 Q8.2 9 10.2 8.2" fill="none" stroke="#ffffff" stroke-width="1.1" stroke-linecap="round" opacity="0.6"/>
  `, look.rarity, size);
};

// --- elixir — tall slim vial (fixed gold liquid; see header) ------------------
const elixir: KindPainter = (look, size) => framedIcon(`
    <path d="M9.2 6.2 L14.8 6.2 L14.8 17 Q14.8 19.8 12 19.8 Q9.2 19.8 9.2 17 Z" fill="${GLASS}" opacity="0.55"/>
    <path d="M9.2 10.6 L14.8 10.6 L14.8 17 Q14.8 19.8 12 19.8 Q9.2 19.8 9.2 17 Z" fill="${ELIXIR_LIQUID}"/>
    <path d="M9.2 6.2 L14.8 6.2 L14.8 17 Q14.8 19.8 12 19.8 Q9.2 19.8 9.2 17 Z" fill="none" stroke="${GLASS_EDGE}" stroke-width="1"/>
    <rect x="8.6" y="5.2" width="6.8" height="1.4" rx="0.7" fill="${GLASS_EDGE}"/>
    <rect x="10.2" y="3" width="3.6" height="2.4" rx="0.9" fill="${CORK}"/>
    <rect x="10.1" y="11.5" width="1.2" height="6.8" rx="0.6" fill="#ffffff" opacity="0.4"/>
  `, look.rarity, size);

// --- flask — wide low flask with a ball stopper (fixed teal; see header) ------
const flask: KindPainter = (look, size) => framedIcon(`
    <path d="M10.7 7.8 L13.3 7.8 L18.7 17.4 Q19.9 20 17.3 20 L6.7 20 Q4.1 20 5.3 17.4 Z" fill="${GLASS}" opacity="0.55"/>
    <path d="M6.5 15.2 L17.5 15.2 L18.7 17.4 Q19.9 20 17.3 20 L6.7 20 Q4.1 20 5.3 17.4 Z" fill="${FLASK_LIQUID}"/>
    <path d="M10.7 7.8 L13.3 7.8 L18.7 17.4 Q19.9 20 17.3 20 L6.7 20 Q4.1 20 5.3 17.4 Z" fill="none" stroke="${GLASS_EDGE}" stroke-width="1"/>
    <rect x="10.4" y="4.8" width="3.2" height="3.4" fill="${GLASS}" opacity="0.85"/>
    <rect x="10" y="4.4" width="4" height="1.2" rx="0.6" fill="${GLASS_EDGE}"/>
    <circle cx="12" cy="3.2" r="1.5" fill="${CORK}"/>
    <circle cx="11.5" cy="2.7" r="0.5" fill="${CORK_DARK}"/>
    <path d="M7.6 14 L10.9 8.6" fill="none" stroke="#ffffff" stroke-width="1.1" stroke-linecap="round" opacity="0.55"/>
  `, look.rarity, size);

// --- scroll — rolled parchment cylinder + red ribbon band ---------------------
const scroll: KindPainter = (look, size) => framedIcon(`
    <rect x="3.6" y="8.8" width="16.8" height="6.8" rx="3.4" fill="${PARCHMENT}"/>
    <rect x="3.6" y="8.8" width="16.8" height="2.2" rx="1.1" fill="${PARCHMENT_LIGHT}"/>
    <ellipse cx="19" cy="12.2" rx="1.9" ry="3.4" fill="${PARCHMENT_LIGHT}"/>
    <ellipse cx="19" cy="12.2" rx="0.9" ry="1.7" fill="${PARCHMENT_DARK}"/>
    <ellipse cx="5" cy="12.2" rx="1.9" ry="3.4" fill="${PARCHMENT_DARK}"/>
    <rect x="10.3" y="8.2" width="3.4" height="8" fill="${RIBBON}"/>
    <polygon points="10.7,16.2 12,20 13.3,16.2" fill="${RIBBON}"/>
    <polygon points="10.7,16.2 12,18 13.3,16.2" fill="#8e2a20"/>
  `, look.rarity, size);

// --- food — drumstick: meat blob + bone with twin knobs -----------------------
const food: KindPainter = (look, size) => framedIcon(`
    <rect x="12.6" y="7.4" width="2.8" height="7" rx="1.4" transform="rotate(-45 14 10.9)" fill="${BONE}"/>
    <circle cx="17.4" cy="5.4" r="1.7" fill="${BONE}"/>
    <circle cx="19.4" cy="7.4" r="1.7" fill="${BONE}"/>
    <path d="M15.2 9.6 Q17 12.6 14.6 16.4 Q11.8 20.6 7.6 19.4 Q3.6 18.2 4.2 13.8 Q4.8 9.6 9.2 8.4 Q13 7.4 15.2 9.6 Z" fill="${MEAT}"/>
    <path d="M9.2 8.4 Q13 7.4 15.2 9.6 Q13.4 8.8 10.4 9.8 Q6.4 11.2 5.4 15.2 Q4.2 14.6 4.2 13.8 Q4.8 9.6 9.2 8.4 Z" fill="${MEAT_LIGHT}"/>
    <path d="M7.8 13.2 Q9.4 12.2 10.8 13.4" fill="none" stroke="#7d4526" stroke-width="1" stroke-linecap="round" opacity="0.7"/>
    <path d="M9 15.8 Q10.6 14.8 12 16" fill="none" stroke="#7d4526" stroke-width="1" stroke-linecap="round" opacity="0.7"/>
  `, look.rarity, size);

// --- bandage — wrapped linen roll + trailing tail ------------------------------
const bandage: KindPainter = (look, size) => framedIcon(`
    <path d="M14.8 8.8 L20.8 10.6 L20.8 15 L15 16.4 Z" fill="${LINEN_DARK}"/>
    <path d="M20.8 10.6 L20.8 15 L18.6 15.55 L18.6 11.25 Z" fill="${LINEN}"/>
    <circle cx="10.4" cy="12.4" r="6.6" fill="${LINEN}"/>
    <path d="M4.4 9.6 Q6 6.6 9.4 5.9" fill="none" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" opacity="0.8"/>
    <path d="M4.2 14.6 Q10.4 12.8 16.6 14.6" fill="none" stroke="${LINEN_DARK}" stroke-width="1" opacity="0.8"/>
    <path d="M5 16.9 Q10.4 15.4 15.8 16.9" fill="none" stroke="${LINEN_DARK}" stroke-width="1" opacity="0.6"/>
    <circle cx="10.4" cy="12.4" r="2.4" fill="none" stroke="${LINEN_DARK}" stroke-width="1.1"/>
    <circle cx="10.4" cy="12.4" r="0.9" fill="${LINEN_DARK}"/>
  `, look.rarity, size);

// --- candy — wrapped sweet: striped ball + twist wrappers ----------------------
const candy: KindPainter = (look, size) => framedIcon(`
    <polygon points="7.6,12 3.4,8.4 4.4,12 3.4,15.6" fill="${CANDY_LIGHT}"/>
    <polygon points="16.4,12 20.6,8.4 19.6,12 20.6,15.6" fill="${CANDY_LIGHT}"/>
    <polygon points="7.6,12 3.4,8.4 4.4,12" fill="${CANDY_DARK}" opacity="0.45"/>
    <polygon points="16.4,12 20.6,15.6 19.6,12" fill="${CANDY_DARK}" opacity="0.45"/>
    <circle cx="12" cy="12" r="4.8" fill="${CANDY}"/>
    <path d="M9 8.4 Q7.8 12 9.4 15.4" fill="none" stroke="#ffffff" stroke-width="1.2" opacity="0.75"/>
    <path d="M12.4 7.4 Q11 12 12.8 16.4" fill="none" stroke="#ffffff" stroke-width="1.2" opacity="0.75"/>
    <path d="M15.4 9 Q14.6 12 15.6 14.8" fill="none" stroke="#ffffff" stroke-width="1.2" opacity="0.75"/>
    <circle cx="10.2" cy="9.8" r="0.8" fill="#ffffff" opacity="0.85"/>
  `, look.rarity, size);

// --- stone_consumable — rounded runestone with a glowing mark ------------------
const stoneConsumable: KindPainter = (look, size) => framedIcon(`
    <path d="M12 3.8 Q17.8 4.4 19.4 9.6 Q20.6 14.4 17.4 18 Q14 21.4 9.2 20 Q4.6 18.6 4.2 13.4 Q3.9 8.6 7.2 5.8 Q9.4 4 12 3.8 Z" fill="${RUNESTONE}"/>
    <path d="M12 3.8 Q9.4 4 7.2 5.8 Q3.9 8.6 4.2 13.4 L6.6 12.6 Q6.6 8.4 9.6 6.4 Z" fill="${RUNESTONE_LIGHT}"/>
    <path d="M9.2 20 Q14 21.4 17.4 18 L15.2 16.4 Q12.8 18.6 9.8 17.8 Z" fill="${RUNESTONE_DARK}"/>
    <path d="M10.2 7.6 L10.2 16.6 M10.2 7.6 L14 9.6 L10.2 11.8 M10.2 11.8 L14.2 16" fill="none" stroke="${RUNE_GLOW}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" opacity="0.35"/>
    <path d="M10.2 7.6 L10.2 16.6 M10.2 7.6 L14 9.6 L10.2 11.8 M10.2 11.8 L14.2 16" fill="none" stroke="${RUNE_GLOW}" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
  `, look.rarity, size);

// --- coin — CURRENCY_ICONS-style gold coin (edge, face, ring, emboss) ----------
const coin: KindPainter = (look, size) => framedIcon(`
    <circle cx="12" cy="12" r="8.4" fill="${COIN_EDGE}" stroke="${COIN_DEEP}" stroke-width="1.2"/>
    <circle cx="12" cy="12" r="6.8" fill="${COIN_FACE}" stroke="${COIN_EDGE}" stroke-width="0.8"/>
    <circle cx="12" cy="12" r="4.7" fill="none" stroke="${COIN_EDGE}" stroke-width="0.7"/>
    <text x="12" y="14.8" text-anchor="middle" font-size="7" font-weight="bold" font-family="serif" fill="${COIN_DEEP}" opacity="0.7">G</text>
    <path d="M7.6 8.6 Q11 5.6 16.4 8.4" fill="none" stroke="rgba(255,255,255,0.45)" stroke-width="1.3" stroke-linecap="round"/>
  `, look.rarity, size);

/** The 8 consumable kinds + coin this family owns (mirrors the IconKind
 *  consumable row + the currency kind — see header for why coin lives here). */
export const CONSUMABLE_ICON_KINDS: readonly IconKind[] = [
  'potion', 'elixir', 'flask', 'scroll', 'food', 'bandage', 'candy', 'stone_consumable', 'coin',
];

const PAINTERS: Readonly<Partial<Record<IconKind, KindPainter>>> = {
  potion, elixir, flask, scroll, food, bandage, candy,
  stone_consumable: stoneConsumable, coin,
};

/** Adapt a look-based painter to the registry's (item, size) contract. */
const asGenerator = (paint: KindPainter): ProceduralIconGenerator =>
  (item: ItemDefinition, size: number) => paint(inferIconLook(item), size);

export const CONSUMABLE_KIND_ICONS: Partial<Record<IconKind, ProceduralIconGenerator>> =
  Object.freeze(Object.fromEntries(
    CONSUMABLE_ICON_KINDS.map((kind) => [kind, asGenerator(PAINTERS[kind]!)]),
  ));
